import { useEffect } from "react";
import { X } from "lucide-react";

/**
 * Glassmorphic legal-text modal (scrollable, cyan-accented).
 *
 * NOTE: The Terms of Service and Privacy Policy text is drafted under the
 * jurisdiction of the United States, operated by Inspireappstudiopro Agency
 * LLC (Nexa Studio). The wording is a reasonable base for a chat app (data
 * retention, no sale of data, no GPS/no calls, minimum age, contact) but
 * MUST be reviewed by a lawyer before commercial use. Do NOT ship to
 * production as legally definitive.
 */
export function LegalModal({ open, title, children, onClose, testId }) {
  useEffect(() => {
    if (!open) return undefined;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = original;
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[95] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={`${testId}-title`}
      data-testid={testId}
    >
      <button
        type="button"
        aria-label="Cerrar"
        onClick={onClose}
        data-testid={`${testId}-overlay`}
        className="absolute inset-0 bg-black/65 backdrop-blur-sm border-0 p-0 cursor-pointer"
      />
      <div
        className="relative q-glass rounded-3xl border border-white/10 shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col"
        style={{ boxShadow: "0 0 60px rgba(0, 240, 255, 0.15)" }}
      >
        <header
          className="flex items-center justify-between px-5 py-4"
          style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}
        >
          <h2
            id={`${testId}-title`}
            className="font-display text-lg sm:text-xl font-bold tracking-tight q-accent-text"
          >
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar modal"
            data-testid={`${testId}-close`}
            className="p-2 rounded-xl transition-colors hover:bg-white/5 text-slate-200"
          >
            <X size={18} />
          </button>
        </header>
        <div
          className="overflow-y-auto px-5 py-4 text-sm text-slate-200 leading-relaxed space-y-3"
          data-testid={`${testId}-body`}
        >
          {children}
        </div>
        <footer
          className="px-5 py-3 text-[10px] uppercase tracking-[0.22em] font-mono text-slate-500"
          style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
        >
          Última revisión pendiente por asesoría legal.
        </footer>
      </div>
    </div>
  );
}

/* -------------------- Content -------------------- */

export const TERMS_TITLE = "Términos de Servicio";
export const PRIVACY_TITLE = "Política de Privacidad";

export function TermsContent() {
  return (
    <>
      <p className="text-[11px] uppercase tracking-widest font-mono text-slate-400">
        Última actualización: 2 de febrero de 2026
      </p>
      <p><strong>1. Aceptación.</strong> Al usar <span className="notranslate" translate="no">Qué Onda Chat</span> aceptas estos Términos. Si no estás de acuerdo, no uses la app.</p>
      <p><strong>2. Edad mínima.</strong> Debes tener al menos 13 años. Si tu país lo exige, 14 o 16 años según jurisdicción local. No creamos cuentas para menores por debajo del mínimo.</p>
      <p><strong>3. Contenido del usuario.</strong> Eres responsable de lo que envías (mensajes, pulsos, avatares, bios). No publiques contenido ilegal, discurso de odio, spam, información privada de terceros ni material sexualmente explícito.</p>
      <p><strong>4. Uso aceptable.</strong> Prohibido el scraping automatizado, la creación de cuentas masivas, la suplantación, la evasión de bloqueos, el envío de malware o el uso comercial no autorizado.</p>
      <p><strong>5. Cuentas de negocio verificadas.</strong> Los negocios verificados aceptan cumplir con las regulaciones aplicables de comercio y publicidad en su jurisdicción.</p>
      <p><strong>6. NexaTokens.</strong> Los NexaTokens son unidades internas de reconocimiento; NO son valores negociables, no tienen equivalencia monetaria ni son transferibles fuera de la app. Nos reservamos el derecho de ajustar tasas.</p>
      <p><strong>7. Sin llamadas ni ubicación.</strong> La app NO ofrece llamadas de voz, video o intercambio de ubicación GPS. Cualquier funcionalidad futura requeriría consentimiento explícito adicional.</p>
      <p><strong>8. Interrupciones y cambios.</strong> Podemos suspender, modificar o descontinuar funciones con aviso razonable. Los datos de tu cuenta se conservarán 30 días para permitir portabilidad antes de eliminación.</p>
      <p><strong>9. Terminación.</strong> Podemos suspender cuentas que violen estas condiciones. Tú puedes eliminar tu cuenta en cualquier momento desde tu perfil.</p>
      <p><strong>10. Limitación de responsabilidad.</strong> La app se ofrece &quot;tal cual&quot;. En la máxima medida permitida por la ley, no somos responsables de daños indirectos derivados del uso.</p>
      <p><strong>11. Ley aplicable y jurisdicción.</strong> Estos Términos se rigen e interpretan conforme a las leyes de los Estados Unidos de América y, cuando aplique, las del estado de constitución de Inspireappstudiopro Agency LLC (Nexa Studio). Cualquier controversia derivada del uso de <span className="notranslate" translate="no">Qué Onda Chat</span> se someterá a los tribunales federales o estatales competentes de los Estados Unidos, salvo derechos irrenunciables del consumidor en tu país de residencia.</p>
      <p><strong>12. Contacto.</strong> Inspireappstudiopro Agency LLC (Nexa Studio) · United States · contacto@inspireappstudiopro.com</p>
      <p className="text-[11px] text-slate-500 mt-4">
        Nota interna: borrador base sujeto a revisión legal antes de uso comercial. Se recomienda auditoría por abogado licenciado en Estados Unidos (state &amp; federal law, incluyendo CCPA/CPRA y equivalentes estatales) antes de operación pública.
      </p>
    </>
  );
}

export function PrivacyContent() {
  return (
    <>
      <p className="text-[11px] uppercase tracking-widest font-mono text-slate-400">
        Última actualización: 2 de febrero de 2026
      </p>
      <p><strong>1. Qué guardamos.</strong> Correo electrónico, contraseña (con hash bcrypt, nunca en texto plano), display name, @OndaTag, avatar (URL de un preset), bio, mensajes que envías, participaciones en OndaPulse, presencia (online/última vez), balance de NexaTokens y su ledger. Guardamos server timestamps.</p>
      <p><strong>2. Qué NO guardamos.</strong> NO guardamos tu ubicación GPS, NO grabamos audio o video de tus llamadas (la app no tiene llamadas), NO recolectamos tu lista de contactos del teléfono, NO leemos otros mensajes de otras apps. Los comandos de voz del Modo Conducción se procesan localmente en tu navegador (Web Speech API) &mdash; no subimos audio a nuestros servidores.</p>
      <p><strong>3. Cookies y almacenamiento local.</strong> Guardamos un token JWT en localStorage para mantener tu sesión. También guardamos preferencias (modo General/Negocio, posición del asistente ONDI, historial del chat con ONDI). Puedes limpiar el almacenamiento local desde tu navegador.</p>
      <p><strong>4. Compartir con terceros.</strong> NO vendemos tus datos. Compartimos únicamente con proveedores necesarios para operar la app (hosting, autenticación de Google si eliges ese método) bajo acuerdos de confidencialidad. Requeriremos orden judicial válida para responder a autoridades.</p>
      <p><strong>5. Autenticación con Google.</strong> Si usas &quot;Continuar con Google&quot;, recibimos de Google tu correo, nombre y foto de perfil pública, únicamente para crear/asociar tu cuenta.</p>
      <p><strong>6. Retención.</strong> Conservamos tu información mientras tu cuenta esté activa. Al eliminarla, se borra en un plazo máximo de 30 días (con excepciones legales de retención cuando aplique).</p>
      <p><strong>7. Derechos.</strong> Puedes ver, editar y eliminar tu perfil en /perfil. Para portabilidad o borrado forzoso escribe a contacto@inspireappstudiopro.com.</p>
      <p><strong>8. Seguridad.</strong> Usamos HTTPS, hashing bcrypt para contraseñas y validación de sesión JWT. Ningún sistema es 100% inviolable; te avisaremos si hubiera un incidente material.</p>
      <p><strong>9. Menores.</strong> No dirigimos la app a menores por debajo de la edad mínima legal aplicable en tu país (mínimo 13). Si detectamos una cuenta de menor no permitido, la eliminaremos.</p>
      <p><strong>10. Cambios.</strong> Publicaremos cambios materiales en la app y por correo. La fecha de última actualización se muestra al pie del modal.</p>
      <p><strong>11. Contacto.</strong> Inspireappstudiopro Agency LLC (Nexa Studio) · United States · contacto@inspireappstudiopro.com</p>
      <p className="text-[11px] text-slate-500 mt-4">
        Nota interna: borrador base sujeto a revisión legal antes de uso comercial. Se recomienda auditoría por abogado licenciado en Estados Unidos (leyes federales de privacidad + estatales como CCPA/CPRA en California, VCDPA en Virginia, CPA en Colorado, etc.) antes de operación pública.
      </p>
    </>
  );
}
